import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';
import { memo, type PropsWithChildren, useContext, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import AssignedToTask from '@/components/AssignedToTask/AssignedToTask';
import KustomerNoteCard from '@/components/KustomerNoteCard';
import NoteCard from '@/components/NoteCard';
import RenderHTML from '@/components/RenderHTML/RenderHTML';
import SnoozedUntil from '@/components/shared/SnoozedUntil';
import TaskDueDate from '@/components/TaskDueDate';
import TaskMenu from '@/components/TaskMenu';
import TaskStatus from '@/components/TaskStatus';
import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import { FormRenderer } from '@/formBuilder';
import NoteForm from '@/forms/NoteForm';
import useAPI from '@/hooks/useAPI';
import PersonContext from '@/pages/Person/PersonContext';
import KustomerNoteRepository from '@/repositories/KustomerNoteRepository';
import NoteRepository from '@/repositories/NoteRepository';
import WorkRepository from '@/repositories/WorkRepository';
import type { Item, ItemPayload } from '@/types/item';
import type { KustomerNote } from '@/types/kustomerNote';
import type { Note } from '@/types/note';
import Badge from '@/ui/components/Badge';
import Card from '@/ui/components/Card';
import Container from '@/ui/components/Container';
import DualTime from '@/ui/components/DualTime/DualTime';
import InputGroup from '@/ui/components/Form/InputGroup';
import Heading from '@/ui/components/Heading/Heading';
import Popover from '@/ui/components/Popover';
import Spinner from '@/ui/components/Spinner';
import TagsEditor from '@/ui/components/TagsEditor/TagsEditor';
import formatStaffName from '@/utils/formatStaffName';
import itemStatusName from '@/utils/itemStatusName';
import statusColor from '@/utils/statusColor';

// Renders an item's answers against the schema they were captured against
// (item.form_schema is pinned at completion time, not the blueprint's live
// form), so its own useForm instance only ever needs to seed once.
const TaskFormAnswers = ({ item }: { item: Item }): JSX.Element | null => {
  const { control } = useForm({ defaultValues: { form_data: item.form_data ?? {} } });

  if (!item.form_schema) return null;

  return (
    <>
      <InlineSection>
        <Heading text="Form" value={3} />
      </InlineSection>
      <InputGroup>
        <FormRenderer schema={item.form_schema} control={control} namespace="form_data" disabled />
      </InputGroup>
      <hr className="mb-2" />
    </>
  );
};

const TaskView = (): JSX.Element | null => {
  const { addMessage, host } = useContext(AppContext);
  const { person } = useContext(PersonContext);
  const workRepo = WorkRepository();
  const noteRepo = NoteRepository();
  const kustomerNoteRepo = KustomerNoteRepository();
  const queryClient = useQueryClient();
  const { itemId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const receivedTaskRef = useRef<Item | null>(location.state?.task ?? null);
  const receivedTask: Item | null = receivedTaskRef.current;
  const isQueryEnabled = !!itemId;

  const updateItem = useMutation({
    mutationFn: async ({ id, values }: { id: string; values: Partial<ItemPayload> }) => {
      return workRepo.updateItem(id, values);
    },
    onSuccess: ({ data }) => {
      data && queryClient.invalidateQueries({ queryKey: ['getItem', data.id] });
      data && addMessage({ level: 'success', text: 'Successfully updated task' });
    }
  });

  const { isPending: isLoadingItem, data: itemResponse } = useAPI({
    queryKey: ['getItem', itemId],
    queryFn: async () => {
      return await workRepo.getItem(itemId as string);
    },
    enabled: isQueryEnabled
  });

  const { isPending: isLoadingNotes, data: notesResponse } = useAPI({
    queryKey: ['getTaskNotes', itemId],
    queryFn: async () => {
      return await noteRepo.getNotes({
        params: {
          content_type_name: 'item',
          object_id: itemId as string,
          ordering: '-created_at',
          page: '1',
          page_size: '100'
        }
      });
    },
    enabled: !!itemId
  });

  const { isPending: isLoadingKustomerNotes, data: kustomerNotesResponse } = useAPI({
    queryKey: ['getTaskKustomerNotes', itemId],
    queryFn: async () => {
      return await kustomerNoteRepo.getItemConversationNotes(itemId as string);
    },
    enabled: !!itemId
  });

  if (isLoadingItem && !receivedTask) {
    return <Spinner />;
  }

  const item = itemResponse?.data ?? receivedTask;

  if (!item && !isLoadingItem) {
    return <Container type="page">Unable to retrieve task</Container>;
  }

  if (item) {
    const noteObjectId = item.id || itemId;

    const handleBack = () => {
      if (location.key !== 'default') {
        navigate(-1);
        return;
      }

      navigate(`/people/${item.person_id}/tasks`);
    };

    const renderDescription = (): JSX.Element => {
      if (item?.description) {
        return <RenderHTML html={item.description} />;
      }

      return <p>No description</p>;
    };

    const renderTags = () => {
      const { blueprint_tags } = item;

      return (
        <>
          {blueprint_tags &&
            !!blueprint_tags.length &&
            blueprint_tags.map((tag, index) => {
              return <Badge key={`tag-${index}`} color={Color.BLUE} text={tag.name} />;
            })}

          <TagsEditor id={item.id} contentType="items" />
        </>
      );
    };

    const handleNoteCreated = () => {
      queryClient.invalidateQueries({ queryKey: ['getTaskNotes', itemId] });
    };

    return (
      <Container type="page">
        <Card>
          <div className="relative">
            <div className="flex justify-between items-start py-1">
              <div className="inline-flex">
                <Heading
                  text={
                    <>
                      <button
                        aria-label="Back"
                        className="w-6 inline-block text-center"
                        type="button"
                        onClick={handleBack}>
                        <FontAwesomeIcon icon={faChevronLeft} className="mr-1 text-xl" />
                      </button>
                      {item.name}
                    </>
                  }
                  value={1}
                />
              </div>
              <TaskMenu item={item} />
            </div>

            <InlineSection>
              <span className="mr-1">
                <Popover
                  buttonColor={statusColor[item.status]}
                  buttonRound
                  buttonSize="xs"
                  popoverButtonText={
                    <span className="flex items-center">{itemStatusName(item.status)}</span>
                  }>
                  <TaskStatus item={item} updateItem={updateItem} />
                </Popover>
              </span>

              <SnoozedUntil item={item} updateItem={updateItem} />

              {item.due_at && (
                <span className="mr-1">
                  <Popover
                    buttonClass="rounded-full"
                    buttonColor={Color.GRAY}
                    buttonRound
                    buttonSize="xs"
                    popoverButtonText={
                      <>
                        <span className="mr-0.5">Due:</span>
                        <DualTime dateTime={new Date(item.due_at)} memberTZ={person?.time_zone} />
                      </>
                    }>
                    <TaskDueDate itemId={item.id} dueDate={item.due_at} updateItem={updateItem} />
                  </Popover>
                </span>
              )}

              <AssignedToTask item={item} />
            </InlineSection>
            <hr className="mb-2" />

            <InlineSection>
              <Heading text="Tags" value={3} />
              {renderTags()}
            </InlineSection>

            <hr className="mb-2" />

            <InlineSection>
              <Heading text="Description" value={3} />
              <div className="mb-2">{renderDescription()}</div>
            </InlineSection>

            <hr className="mb-2" />

            <TaskFormAnswers item={item} />

            <InlineSection>
              <Heading text="Created" value={3} />
              <div className="flex flex-wrap items-center">
                <span className="mr-1">
                  {item?.created_by && <>{formatStaffName({ staff: item.created_by })}</>}
                </span>
                {item?.created_at && (
                  <DualTime dateTime={new Date(item.created_at)} memberTZ={person?.time_zone} />
                )}
              </div>
            </InlineSection>

            <InlineSection>
              <Heading text="Completed" value={3} />
              <div className="flex flex-wrap items-center">
                <span className="mr-1">
                  {item?.last_completed_by ? (
                    <>{formatStaffName({ staff: item.last_completed_by })}</>
                  ) : (
                    'None'
                  )}
                </span>
                {item?.last_completed_at && (
                  <DualTime
                    dateTime={new Date(item.last_completed_at)}
                    memberTZ={person?.time_zone}
                  />
                )}
              </div>
            </InlineSection>

            <hr className="mb-2" />

            <div className="mb-2">
              <Heading text="Notes" value={3} />

              {noteObjectId && (
                <div className="mb-4">
                  <NoteForm
                    objectId={noteObjectId}
                    contentTypeName="item"
                    withHeading={false}
                    successCallback={handleNoteCreated}
                  />
                </div>
              )}

              {isLoadingNotes || isLoadingKustomerNotes ? (
                <Spinner label="Loading notes..." />
              ) : notesResponse?.results.length || kustomerNotesResponse?.data?.length ? (
                <ul role="list">
                  {notesResponse?.results.map((note: Note) => (
                    <li key={note.id}>
                      <NoteCard note={note} />
                    </li>
                  ))}

                  {kustomerNotesResponse?.data?.map((note: KustomerNote) => (
                    <li key={note.id}>
                      <KustomerNoteCard
                        note={note}
                        host={host}
                        personKustomerId={person?.kustomer_id}
                        memberTimeZone={person?.time_zone}
                      />
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No notes</p>
              )}
            </div>
          </div>
        </Card>
      </Container>
    );
  }

  return null;
};

const InlineSection = ({ children }: PropsWithChildren): JSX.Element => (
  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-2">{children}</div>
);

export default memo(TaskView);
